import { useState, useEffect } from 'react'
import { Tabs, Search, List, Tag, Button, Empty, Loading, Toast, PullRefresh, Dialog } from 'react-vant'
import { ArrowLeft, Plus, Description, ChatO, BullhornO, QuestionO, UserO, DeleteO, StarO, Star, LikeO, Like } from '@react-vant/icons'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import request from '../../api/request'

export default function HubIndex() {
    const navigate = useNavigate()
    const { userInfo } = useSelector(state => state.user)
    const [activeTab, setActiveTab] = useState('all')
    const [loading, setLoading] = useState(false)
    const [refreshing, setRefreshing] = useState(false)
    const [posts, setPosts] = useState([])
    const [page, setPage] = useState(1)
    const [finished, setFinished] = useState(false)
    const [searchText, setSearchText] = useState('')

    const handleDeletePost = (e, postId) => {
        e.stopPropagation(); // 阻止进入详情页
        Dialog.confirm({
            title: '确认删除',
            message: '确定要删除这条动态吗？删除后不可恢复。',
            onConfirm: async () => {
                try {
                    await request.delete(`/hub/posts/${postId}/`);
                    setPosts(prev => prev.filter(p => p.id !== postId));
                    Toast.success('已删除');
                } catch (e) {
                    Toast.fail(e.message || '删除失败');
                }
            },
        });
    }

    const handleLikePost = async (e, postId) => {
        e.stopPropagation();
        try {
            const res = await request.post(`/hub/posts/${postId}/like/`);
            setPosts(prev => prev.map(p => p.id === postId ? { ...p, like_count: res.like_count } : p));
        } catch (e) {
            console.error(e);
        }
    }

    const handleTogglePin = async (e, postId) => {
        e.stopPropagation();
        try {
            const res = await request.post(`/hub/posts/${postId}/pin/`);
            setPosts(prev => prev.map(p => p.id === postId ? { ...p, is_pinned: res.is_pinned } : p));
            Toast.info(res.message);
        } catch (e) {
            Toast.fail(e.message || '操作失败');
        }
    }

    const fetchPosts = async (isRefresh = false) => {
        const currentPage = isRefresh ? 1 : page
        try {
            const params = {
                page: currentPage,
                page_size: 10,
                search: searchText || undefined,
            }
            if (activeTab === 'official') {
                params.is_official = true
            } else if (activeTab !== 'all') {
                params.category = activeTab
            }

            const res = await request.get('/hub/posts/', { params })
            if (res.items) {
                if (isRefresh) {
                    setPosts(res.items)
                    setPage(2)
                    setFinished(res.items.length >= res.total)
                } else {
                    setPosts([...posts, ...res.items])
                    setPage(currentPage + 1)
                    setFinished(posts.length + res.items.length >= res.total)
                }
            }
        } catch (e) {
            console.error(e)
            Toast.fail('获取数据失败')
        } finally {
            setLoading(false)
            setRefreshing(false)
        }
    }

    useEffect(() => {
        setLoading(true)
        fetchPosts(true)
    }, [activeTab, searchText])

    const onRefresh = async () => {
        setRefreshing(true)
        await fetchPosts(true)
    }

    const onLoad = async () => {
        if (loading || finished || refreshing) return
        await fetchPosts()
    }

    const getCategoryIcon = (category) => {
        switch (category) {
            case 'notice': return <BullhornO color="#fff" size={16} />
            case 'document': return <Description color="#fff" size={16} />
            case 'question': return <QuestionO color="#fff" size={16} />
            default: return <ChatO color="#fff" size={16} />
        }
    }

    const getCategoryColor = (category) => {
        switch (category) {
            case 'notice': return 'linear-gradient(135deg, #ff5e1e, #e11d48)'
            case 'document': return 'linear-gradient(135deg, #00b578, #34d399)'
            case 'question': return 'linear-gradient(135deg, #fa8c16, #fbbf24)'
            default: return 'linear-gradient(135deg, #00b578, #34d399)'
        }
    }

    return (
        <div style={{ background: '#f8fafc', minHeight: '100vh', paddingBottom: 80, fontFamily: '"Inter", "PingFang SC", "Microsoft YaHei", sans-serif' }}>
            {/* 沉浸式 Header Banner */}
            <div style={{ 
                background: 'linear-gradient(135deg, #055e43 0%, #076f51 50%, #0c8c68 100%)', 
                padding: '50px 24px 60px', 
                color: '#fff',
                position: 'relative'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                        <h2 style={{ margin: 0, fontSize: 32, fontWeight: 800, letterSpacing: '-1px' }}>校园广场</h2>
                        <p style={{ margin: '8px 0 0', opacity: 0.8, fontSize: 14, fontWeight: 500 }}>发现校园精彩，分享教学点滴</p>
                    </div>
                </div>
            </div>

            {/* 悬浮搜索框区 */}
            <div style={{ 
                margin: '-30px 16px 12px', 
                position: 'relative', 
                zIndex: 10,
                borderRadius: '20px', 
                overflow: 'hidden', 
                boxShadow: '0 12px 30px rgba(0,0,0,0.1)',
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.8)'
            }}>
                <Search
                    value={searchText}
                    placeholder="搜索动态、文档或公告..."
                    onChange={setSearchText}
                    background="transparent"
                    shape="round"
                />
            </div>

            {/* 高级分类 Tabs (胶囊样式) */}
            <div style={{ 
                position: 'sticky', 
                top: 0, 
                zIndex: 99, 
                background: 'rgba(248, 250, 252, 0.95)', 
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                padding: '4px 0 8px',
                borderBottom: '1px solid rgba(0,0,0,0.05)'
            }}>
                <Tabs 
                    active={activeTab} 
                    onChange={setActiveTab} 
                    background="transparent"
                    type="line"
                    color="#04523c"
                    titleActiveColor="#04523c"
                    titleInactiveColor="#94a3b8"
                    lineWidth={20}
                    lineHeight={4}
                    border={false}
                >
                    <Tabs.TabPane name="all" title={<span style={{ fontWeight: activeTab === 'all' ? 800 : 500, fontSize: 16 }}>推荐</span>} />
                    <Tabs.TabPane name="official" title={<span style={{ fontWeight: activeTab === 'official' ? 800 : 500, fontSize: 16 }}>公文</span>} />
                    <Tabs.TabPane name="discussion" title={<span style={{ fontWeight: activeTab === 'discussion' ? 800 : 500, fontSize: 16 }}>沙龙</span>} />
                    <Tabs.TabPane name="question" title={<span style={{ fontWeight: activeTab === 'question' ? 800 : 500, fontSize: 16 }}>问答</span>} />
                </Tabs>
            </div>

            <PullRefresh onRefresh={onRefresh} refreshing={refreshing}>
                <div style={{ padding: '4px 16px 20px' }}>
                    {posts.length === 0 && !loading ? (
                        <div style={{ marginTop: '40px', padding: '40px 20px', background: '#fff', borderRadius: '24px', textAlign: 'center', boxShadow: '0 8px 30px rgba(0,0,0,0.03)' }}>
                            <Empty description="广场空空如也，快来发布第一条动态吧！" imageSize={120} />
                        </div>
                    ) : (
                        <List finished={finished} onLoad={onLoad} loading={loading} finishedText="没有更多内容了">
                            {posts.map(post => (
                                <div 
                                    key={post.id} 
                                    onClick={() => navigate(`/hub/post/${post.id}`)}
                                    style={{ 
                                        marginBottom: 16, 
                                        borderRadius: 20, 
                                        background: '#fff',
                                        padding: '20px',
                                        boxShadow: '0 8px 24px rgba(149, 157, 165, 0.08)',
                                        border: '1px solid rgba(255,255,255,0.8)',
                                        transition: 'transform 0.2s',
                                        position: 'relative',
                                        overflow: 'hidden'
                                    }}
                                >
                                    {/* 顶部标签 */}
                                    {post.is_pinned && (
                                        <div style={{ position: 'absolute', top: 0, right: 0, background: 'linear-gradient(135deg, #055e43, #0c8c68)', color: '#fff', fontSize: 10, padding: '4px 12px', borderBottomLeftRadius: 16, fontWeight: 700 }}>
                                            置顶
                                        </div>
                                    )}

                                    <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                                        {/* 发帖人头像占位 */}
                                        <div style={{ width: 42, height: 42, borderRadius: '50%', background: 'linear-gradient(135deg, #fdfbfb 0%, #e2e8f0 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.05)' }}>
                                            <span style={{ fontSize: 16, color: '#64748b', fontWeight: 700 }}>{post.author_name ? post.author_name[0] : '教'}</span>
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                <span style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>{post.author_name}</span>
                                                {post.is_official && <Tag color="#e11d48" plain size="mini" style={{ borderRadius: 4, padding: '0 4px' }}>官方</Tag>}
                                            </div>
                                            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{new Date(post.created_at).toLocaleDateString()}</div>
                                        </div>
                                    </div>

                                    {/* 标题区与分类图标 */}
                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 8 }}>
                                        <div style={{ 
                                            width: 28, height: 28, borderRadius: 8, 
                                            background: getCategoryColor(post.category),
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            flexShrink: 0, marginTop: 2,
                                            boxShadow: '0 4px 10px rgba(0,0,0,0.1)'
                                        }}>
                                            {getCategoryIcon(post.category)}
                                        </div>
                                        <h4 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#334155', lineHeight: 1.4 }}>
                                            {post.title}
                                        </h4>
                                    </div>

                                    {/* 摘要正文 */}
                                    <p style={{ color: '#475569', fontSize: 14, margin: '8px 0 12px', lineHeight: 1.6, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                        {post.content}
                                    </p>

                                    {/* 附件信息 */}
                                    {post.attachments?.length > 0 && (
                                        <div style={{ 
                                            background: '#f8fafc', padding: '8px 12px', borderRadius: 8, 
                                            fontSize: 12, color: '#056b4f', display: 'flex', alignItems: 'center', gap: 6,
                                            marginBottom: 12, border: '1px solid #f1f5f9'
                                        }}>
                                            <Description size={16} /> 包含 {post.attachments.length} 个附件文档
                                        </div>
                                    )}

                                    {/* 底部统计信息 */}
                                    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 16, borderTop: '1px solid #f1f5f9', paddingTop: 12 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#94a3b8', fontSize: 12, fontWeight: 500 }}>
                                            <UserO size={14} /> {post.view_count}
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#94a3b8', fontSize: 12, fontWeight: 500 }}>
                                            <ChatO size={14} /> {post.comment_count}
                                        </div>
                                        <div 
                                            onClick={(e) => handleLikePost(e, post.id)}
                                            style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#ff7675', fontSize: 12, fontWeight: 500 }}
                                        >
                                            <LikeO size={14} /> {post.like_count || 0}
                                        </div>
                                        {userInfo?.role === 'admin' && (
                                            <div 
                                                onClick={(e) => handleTogglePin(e, post.id)}
                                                style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#fdcb6e', fontSize: 12, fontWeight: 500 }}
                                            >
                                                {post.is_pinned ? <Star size={14} /> : <StarO size={14} />} {post.is_pinned ? '取消置顶' : '置顶'}
                                            </div>
                                        )}
                                        {(userInfo?.role === 'admin' || userInfo?.id === post.author_id) && (
                                            <div 
                                                onClick={(e) => handleDeletePost(e, post.id)}
                                                style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#e11d48', fontSize: 12, fontWeight: 500, marginLeft: 'auto' }}
                                            >
                                                <DeleteO size={14} /> 删除
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </List>
                    )}
                </div>
            </PullRefresh>

            {/* 悬浮发布按钮 */}
            <div 
                onClick={() => navigate('/hub/create')}
                style={{
                    position: 'fixed',
                    bottom: '90px',
                    right: '20px',
                    width: '56px',
                    height: '56px',
                    borderRadius: '28px',
                    background: 'linear-gradient(135deg, #055e43, #0c8c68)',
                    boxShadow: '0 8px 20px rgba(5, 107, 79, 0.4)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    zIndex: 100,
                    cursor: 'pointer',
                    transition: 'transform 0.2s',
                }}
            >
                <Plus size={28} />
            </div>
        </div>
    )
}
